import { Chart, Plugin } from "chart.js";
import type { IChartWithCrosshair } from "../external-tooltip";

const chartsWithoutFrequencyProfile = new WeakSet<object>();

export const drawWithoutFrequencyProfile = (chart: Pick<Chart, "draw">): void => {
  chartsWithoutFrequencyProfile.add(chart);
  try {
    chart.draw();
  } finally {
    chartsWithoutFrequencyProfile.delete(chart);
  }
};

type LineStyle = {
  strokeStyle: string;
  lineWidth: number;
  lineDash: number[];
};

interface IDrawConnectingLinesPluginOptions {
  xOffset: number;
  yOffset: number;
  showActiveCascadeLine?: boolean;
  showGridConnections?: boolean;
  profileNativeX?: number | null;
  grid?: {
    xTicks: number[];
    yTicks: number[];
    xMin: number;
    yBase: number;
  };
}

interface IDataPoint {
  x: number;
  y: number;
}

type PreparedDataset = {
  points: IDataPoint[];
  minX: number;
  maxX: number;
  maxY: number;
};

type PixelPoint = {
  x: number;
  y: number;
};

const CONNECTION_LINE_STYLE: LineStyle = {
  strokeStyle: "rgba(0, 0, 0, 0.5)",
  lineWidth: 1,
  lineDash: [5, 5],
};

const CASCADE_LINE_STYLE: LineStyle = {
  strokeStyle: "rgba(255, 0, 0, 0.7)",
  lineWidth: 2,
  lineDash: [],
};

const PROFILE_LINE_STYLE: LineStyle = {
  strokeStyle: "#00a000",
  lineWidth: 2.5,
  lineDash: [],
};

const isDataPoint = (point: unknown): point is IDataPoint => {
  if (point === null || typeof point !== "object") {
    return false;
  }

  const candidate = point as Record<string, unknown>;

  return typeof candidate.x === "number" && typeof candidate.y === "number";
};

const prepareDatasets = (datasets: Array<{ data: unknown[] }>): PreparedDataset[] =>
  datasets.map(dataset => {
    const points = Array.isArray(dataset.data) ? dataset.data.filter(isDataPoint) : [];

    let minX = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    points.forEach(point => {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    });

    return {
      points,
      minX,
      maxX,
      maxY,
    };
  });

const applyLineStyle = (ctx: CanvasRenderingContext2D, style: LineStyle): void => {
  ctx.strokeStyle = style.strokeStyle;
  ctx.lineWidth = style.lineWidth;
  ctx.setLineDash(style.lineDash);
};

const drawPath = (ctx: CanvasRenderingContext2D, points: PixelPoint[]): void => {
  if (points.length < 2) {
    return;
  }

  ctx.beginPath();

  points.forEach((point, index) => {
    if (index === 0) {
      ctx.moveTo(point.x, point.y);
      return;
    }

    ctx.lineTo(point.x, point.y);
  });

  ctx.stroke();
};

const findClosestPointByX = (points: IDataPoint[], expectedX: number): IDataPoint | null => {
  if (points.length === 0) {
    return null;
  }

  let left = 0;
  let right = points.length - 1;

  while (left <= right) {
    const middle = Math.floor((left + right) / 2);
    const pointX = points[middle].x;

    if (pointX === expectedX) {
      return points[middle];
    }

    if (pointX < expectedX) {
      left = middle + 1;
    } else {
      right = middle - 1;
    }
  }

  if (left >= points.length) {
    return points[points.length - 1];
  }

  if (right < 0) {
    return points[0];
  }

  const leftPoint = points[left];
  const rightPoint = points[right];

  return Math.abs(leftPoint.x - expectedX) < Math.abs(rightPoint.x - expectedX) ? leftPoint : rightPoint;
};

const interpolatePointByX = (points: IDataPoint[], expectedX: number): IDataPoint | null => {
  if (points.length === 0 || expectedX < points[0].x || expectedX > points[points.length - 1].x) {
    return null;
  }

  let left = 0;
  let right = points.length - 1;

  while (left <= right) {
    const middle = Math.floor((left + right) / 2);
    const point = points[middle];

    if (point.x === expectedX) {
      return Number.isFinite(point.y) ? point : null;
    }

    if (point.x < expectedX) {
      left = middle + 1;
    } else {
      right = middle - 1;
    }
  }

  const before = points[right];
  const after = points[left];

  if (!before || !after || !Number.isFinite(before.y) || !Number.isFinite(after.y)) {
    return null;
  }

  const ratio = (expectedX - before.x) / (after.x - before.x);
  return { x: expectedX, y: before.y + ratio * (after.y - before.y) };
};

export const DrawConnectingLinesPlugin: Plugin<"line", IDrawConnectingLinesPluginOptions> = {
  id: "drawConnectingLines",
  afterDatasetsDraw(chart, args, options) {
    if (!options) {
      return;
    }

    const {
      ctx,
      scales: { x, y },
    } = chart;

    if (!ctx || !x || !y) {
      return;
    }

    const preparedDatasets = prepareDatasets(chart.data.datasets);
    const activeElements = chart.getActiveElements();
    const xOffset = options.xOffset ?? 0;
    const yOffset = options.yOffset ?? 0;
    const showActiveCascadeLine = options.showActiveCascadeLine ?? true;
    const showGridConnections = options.showGridConnections ?? true;
    const profileNativeX = options.profileNativeX ?? null;

    const toPixels = (xValue: number, yValue: number): PixelPoint => ({
      x: x.getPixelForValue(xValue),
      y: y.getPixelForValue(yValue),
    });

    const buildConnectionPath = (
      ticks: number[],
      getBasePoint: (tick: number) => IDataPoint,
      getShiftedPoint: (tick: number, datasetIndex: number) => IDataPoint,
    ): void => {
      for (let tickIndex = 0; tickIndex < ticks.length; tickIndex += 1) {
        const tick = ticks[tickIndex];
        const path: PixelPoint[] = [];
        const basePoint = getBasePoint(tick);

        path.push(toPixels(basePoint.x, basePoint.y));

        for (let dsIndex = 1; dsIndex < preparedDatasets.length; dsIndex += 1) {
          const dataset = preparedDatasets[dsIndex];
          const shiftedPoint = getShiftedPoint(tick, dsIndex);

          if (shiftedPoint.x > dataset.maxX || shiftedPoint.y > dataset.maxY) {
            continue;
          }

          path.push(toPixels(shiftedPoint.x, shiftedPoint.y));
        }

        drawPath(ctx, path);
      }
    };

    const drawDatasetConnections = (): void => {
      const zeroYVisible = typeof y.min === "number" && typeof y.max === "number" && y.min < 0 && y.max > 0;
      const xMinValue = options.grid?.xMin ?? x.min;
      const yBaseValue = options.grid?.yBase ?? (zeroYVisible ? 0 : y.min);
      const xTicks = options.grid?.xTicks ?? x.ticks.map(tick => tick.value);
      const yTicks = options.grid?.yTicks ?? y.ticks.map(tick => tick.value);

      buildConnectionPath(
        xTicks,
        tick => ({ x: tick, y: yBaseValue }),
        (tick, datasetIndex) => ({
          x: tick + datasetIndex * xOffset,
          y: yBaseValue + datasetIndex * yOffset,
        }),
      );

      const nonNegativeYTicks: number[] = [];
      for (let index = 0; index < yTicks.length; index += 1) {
        if (yTicks[index] >= 0) {
          nonNegativeYTicks.push(yTicks[index]);
        }
      }

      buildConnectionPath(
        nonNegativeYTicks,
        tick => ({ x: xMinValue, y: tick }),
        (tick, datasetIndex) => ({
          x: xMinValue + datasetIndex * xOffset,
          y: tick + datasetIndex * yOffset,
        }),
      );
    };

    const drawCascadeLine = (): void => {
      const crosshair = (chart as IChartWithCrosshair).crosshair;
      const syncedPoint = crosshair?.locked ? crosshair.syncedPoint : null;

      if (syncedPoint) {
        const points: PixelPoint[] = [];

        for (let dsIndex = 0; dsIndex < preparedDatasets.length; dsIndex += 1) {
          if (!chart.isDatasetVisible(dsIndex)) {
            continue;
          }

          const nativeX = crosshair?.syncCursorValues ? crosshair.syncCursorValues[dsIndex] : syncedPoint.nativeX;

          if (typeof nativeX !== "number" || !Number.isFinite(nativeX)) {
            continue;
          }

          const point = interpolatePointByX(preparedDatasets[dsIndex].points, nativeX + dsIndex * xOffset);

          if (point) {
            points.push(toPixels(point.x, point.y));
          }
        }

        drawPath(ctx, points);
        return;
      }

      if (activeElements.length === 0) {
        return;
      }

      const { datasetIndex, index } = activeElements[0];
      const activePoint = chart.data.datasets[datasetIndex]?.data[index];

      if (!isDataPoint(activePoint)) {
        return;
      }

      const baseX = activePoint.x - datasetIndex * xOffset;
      const points: PixelPoint[] = [];

      for (let dsIndex = 0; dsIndex < preparedDatasets.length; dsIndex += 1) {
        const dataset = preparedDatasets[dsIndex];
        const expectedX = baseX + dsIndex * xOffset;
        const point = findClosestPointByX(dataset.points, expectedX);

        if (!point) {
          continue;
        }

        points.push(toPixels(point.x, point.y));
      }

      drawPath(ctx, points);
    };

    const drawFrequencyProfile = (): void => {
      if (profileNativeX == null || !Number.isFinite(profileNativeX)) {
        return;
      }

      const points: PixelPoint[] = [];

      for (let datasetIndex = 0; datasetIndex < preparedDatasets.length; datasetIndex += 1) {
        const dataset = preparedDatasets[datasetIndex];
        const expectedX = profileNativeX + datasetIndex * xOffset;

        if (expectedX < dataset.minX || expectedX > dataset.maxX) {
          continue;
        }

        const point = findClosestPointByX(dataset.points, expectedX);

        if (point) {
          points.push(toPixels(point.x, point.y));
        }
      }

      drawPath(ctx, points);

      for (const point of points) {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const { left, top, right, bottom } = chart.chartArea;
    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top, right - left, bottom - top);
    ctx.clip();

    try {
      if (showGridConnections) {
        applyLineStyle(ctx, CONNECTION_LINE_STYLE);
        drawDatasetConnections();
      }

      if (showActiveCascadeLine) {
        applyLineStyle(ctx, CASCADE_LINE_STYLE);
        drawCascadeLine();
      }

      if (profileNativeX != null && !chartsWithoutFrequencyProfile.has(chart)) {
        applyLineStyle(ctx, PROFILE_LINE_STYLE);
        ctx.fillStyle = PROFILE_LINE_STYLE.strokeStyle;
        drawFrequencyProfile();
      }
    } finally {
      ctx.restore();
    }
  },
};
